import api from './api';
import authService from './authService';

/**
 * Diagnostic Service for testing API connectivity and permissions
 * Used to identify network issues, authentication problems, and permission restrictions
 */
class DiagnosticService {
  /**
   * Test basic connectivity to the API server
   * @returns {Promise<Object>} Response with connection status
   */
  async testApiConnection() {
    try {
      // First try the health endpoint
      const response = await api.get('/health', { timeout: 5000 });
      return {
        success: true,
        status: response.status,
        message: 'Successfully connected to API server',
        data: response.data
      };
    } catch (error) {
      // If health endpoint fails, try the root API path
      try {
        const rootResponse = await api.get('/', { timeout: 5000 });
        return {
          success: true,
          status: rootResponse.status,
          message: 'Connected to API server root endpoint',
          data: rootResponse.data
        };
      } catch (rootError) {
        return this.handleConnectionError(error, 'API Server');
      }
    }
  }

  /**
   * Discover available API endpoints
   * @returns {Promise<Object>} Response with available API endpoints
   */
  async discoverApiEndpoints() {
    try {
      const response = await api.get('/', { timeout: 5000 });
      return {
        success: true,
        status: response.status,
        message: 'Successfully discovered API endpoints',
        endpoints: response.data?.availableEndpoints || response.data?.routes || {}
      };
    } catch (error) {
      return this.handleConnectionError(error, 'API Discovery');
    }
  }

  /**
   * Test authentication by validating the current auth token
   * @returns {Promise<Object>} Response with authentication status
   */
  async testAuthentication() {
    try {
      const token = authService.getToken();
      
      if (!token) {
        return {
          success: false,
          status: 401,
          message: 'No authentication token found',
          error: 'TOKEN_MISSING'
        };
      }
      
      // First, discover available endpoints to find the correct authentication path
      const discovery = await this.discoverApiEndpoints();
      let authEndpoint = '/auth/validate-token';
      
      // Try verify-token endpoint from discovered endpoints
      if (discovery.success && discovery.endpoints) {
        const endpoints = discovery.endpoints;
        if (endpoints.auth) {
          // The diagnostics indicated /api/auth/* is available
          authEndpoint = '/auth/validate-token';
        }
      }
      
      // Try the token validation
      try {
        const response = await api.post(authEndpoint, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000
        });
        
        return {
          success: true,
          status: response.status,
          message: 'Authentication successful',
          data: {
            user: response.data?.user,
            tokenValid: true
          }
        };
      } catch (authError) {
        // If validate-token fails, try a different endpoint
        try {
          const fallbackResponse = await api.get('/auth/status', {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000
          });
          
          return {
            success: true,
            status: fallbackResponse.status,
            message: 'Authentication check successful with fallback endpoint',
            data: fallbackResponse.data
          };
        } catch (fallbackError) {
          return this.handleConnectionError(fallbackError, 'Authentication Service');
        }
      }
    } catch (error) {
      return this.handleConnectionError(error, 'Authentication Service');
    }
  }
  
  /**
   * Test tenant access permissions
   * @returns {Promise<Object>} Response with tenant access status
   */
  async testTenantAccess() {
    try {
      const token = authService.getToken();
      const currentUser = authService.getCurrentUser();
      
      if (!token) {
        return {
          success: false,
          status: 401,
          message: 'No authentication token found',
          error: 'TOKEN_MISSING'
        };
      }
      
      // Based on the error, the endpoint requires a tenant ID
      let tenantId = currentUser?.tenantId || '';
      
      if (!tenantId && currentUser?.role === 'superadmin') {
        // Superadmins might not have a specific tenant ID
        // Try to get the first accessible tenant
        try {
          const tenantsResponse = await api.get('/tenants', {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000
          });
          
          if (tenantsResponse.data?.tenants?.length > 0) {
            tenantId = tenantsResponse.data.tenants[0]._id || tenantsResponse.data.tenants[0].id;
          }
        } catch (error) {
          // Ignore this error, proceed with empty tenant ID
        }
      }
      
      // Now try to access tenant with ID if available
      if (tenantId) {
        try {
          const response = await api.get(`/tenants/${tenantId}/access-check`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000
          });
          
          return {
            success: true,
            status: response.status,
            message: 'Tenant access check successful',
            data: {
              role: currentUser?.role,
              permissions: response.data?.permissions || [],
              tenantAccess: response.data?.hasAccess || false,
              tenantId
            }
          };
        } catch (accessError) {
          return this.handleConnectionError(accessError, 'Tenant Access Check');
        }
      } else {
        return {
          success: false,
          status: 400,
          message: 'No tenant ID available to check access',
          error: 'TENANT_ID_MISSING',
          data: { role: currentUser?.role }
        };
      }
    } catch (error) {
      return this.handleConnectionError(error, 'Tenant Service');
    }
  }
  
  /**
   * Test superadmin-specific permissions
   * @returns {Promise<Object>} Response with superadmin permissions status
   */
  async testSuperAdminAccess() {
    try {
      const token = authService.getToken();
      const currentUser = authService.getCurrentUser();
      
      if (!token) {
        return {
          success: false,
          status: 401,
          message: 'No authentication token found',
          error: 'TOKEN_MISSING'
        };
      }
      
      if (currentUser?.role !== 'superadmin') {
        return {
          success: false,
          status: 403,
          message: 'User is not a superadmin',
          error: 'INSUFFICIENT_PERMISSIONS',
          data: { role: currentUser?.role }
        };
      }
      
      // Try accessing a superadmin-only endpoint
      try {
        const response = await api.get('/superadmin/status', {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000
        });
        
        return {
          success: true,
          status: response.status,
          message: 'Superadmin access verified',
          data: response.data
        };
      } catch (error) {
        // If specific endpoint fails, try another superadmin endpoint
        try {
          const fallbackResponse = await api.get('/superadmin', {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000
          });
          
          return {
            success: true,
            status: fallbackResponse.status,
            message: 'Superadmin access verified with fallback endpoint',
            data: fallbackResponse.data
          };
        } catch (fallbackError) {
          return this.handleConnectionError(fallbackError, 'SuperAdmin Service');
        }
      }
    } catch (error) {
      return this.handleConnectionError(error, 'SuperAdmin Service');
    }
  }
  
  /**
   * Comprehensive system check to identify connectivity issues
   * @returns {Promise<Object>} Comprehensive diagnostic results
   */
  async runFullDiagnostic() {
    const results = {
      apiConnection: await this.testApiConnection(),
      authentication: await this.testAuthentication(),
      tenantAccess: await this.testTenantAccess(),
      superAdminAccess: await this.testSuperAdminAccess(),
      timestamp: new Date().toISOString()
    };
    
    // Determine overall system status
    results.overallStatus = this.determineOverallStatus(results);
    
    return results;
  }
  
  /**
   * Handle connection errors and provide meaningful diagnostics
   * @param {Error} error - The error object from axios
   * @param {string} service - The service being tested
   * @returns {Object} Formatted error response
   */
  handleConnectionError(error, service) {
    const errorResponse = {
      success: false,
      service
    };
    
    if (error.response) {
      // Server responded with error status
      errorResponse.status = error.response.status;
      errorResponse.data = error.response.data;
      errorResponse.message = `${service} returned error: ${error.response.status} ${error.response.statusText}`;
      
      // Add specific error type based on status code
      if (error.response.status === 401) {
        errorResponse.error = 'UNAUTHORIZED';
      } else if (error.response.status === 403) {
        errorResponse.error = 'FORBIDDEN';
      } else if (error.response.status === 404) {
        errorResponse.error = 'NOT_FOUND';
      } else if (error.response.status >= 500) {
        errorResponse.error = 'SERVER_ERROR';
      }
    } else if (error.request) {
      // Request made but no response received
      errorResponse.status = 0;
      errorResponse.error = 'NO_RESPONSE';
      errorResponse.message = `${service} connection failed: No response received from server`;
    } else {
      // Error in setting up the request
      errorResponse.status = -1;
      errorResponse.error = 'REQUEST_SETUP_ERROR';
      errorResponse.message = `${service} connection failed: ${error.message}`;
    }
    
    // Add network information if available
    if (error.code === 'ECONNABORTED') {
      errorResponse.error = 'TIMEOUT';
      errorResponse.message = `${service} connection timed out`;
    }
    
    // Log the complete error for debugging
    console.error(`Diagnostic error with ${service}:`, error);
    
    return errorResponse;
  }
  
  /**
   * Determine overall system status based on individual test results
   * @param {Object} results - The results of all diagnostic tests
   * @returns {Object} Overall status assessment
   */
  determineOverallStatus(results) {
    const status = {
      connected: results.apiConnection.success,
      authenticated: results.authentication.success,
      hasAccess: results.tenantAccess.success && results.tenantAccess.data?.tenantAccess,
      isSuperAdmin: results.superAdminAccess.success,
      blockers: []
    };
    
    // Identify blockers
    if (!status.connected) {
      status.blockers.push({
        type: 'CONNECTION',
        message: 'Cannot connect to API server. Check network connection and API server status.'
      });
    }
    
    if (!status.authenticated) {
      status.blockers.push({
        type: 'AUTHENTICATION',
        message: 'Authentication failed. Try logging in again or check if token has expired.'
      });
    }
    
    if (!status.hasAccess && status.authenticated) {
      status.blockers.push({
        type: 'PERMISSION',
        message: 'You do not have sufficient permissions to access tenant data.'
      });
    }
    
    return status;
  }
}

export default new DiagnosticService();
